<template>
  <div class="simulation-compare-page">
    <h1>{{ t('simulation.compare.title') }}</h1>
    <div v-if="isLoading" class="loading-message">
      <p>{{ t('simulation.compare.loading') }}</p>
      <div class="spinner"></div>
    </div>
    <div v-else-if="error" class="error-message">
      <p>{{ t('simulation.compare.error', { message: error }) }}</p>
      <router-link to="/simulation/history" class="btn btn-secondary">{{ t('simulation.compare.backToHistory') }}</router-link>
    </div>
    <div v-else-if="simulationsToCompare.length > 0">
      <p class="comparison-intro">
        {{ t('simulation.compare.intro', { count: simulationsToCompare.length }) }}
      </p>

      <section class="compare-section">
        <h2>{{ t('simulation.compare.sections.coreIndicators') }}</h2>
        <div v-if="indicatorTableData.length > 0" class="table-responsive">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>{{ t('simulation.compare.table.metric') }}</th>
                <th v-for="sim in simulationsToCompare" :key="sim.id" class="sim-name-header" :title="sim.name">
                  {{ sim.name.length > 20 ? sim.name.substring(0, 17) + '…' : sim.name }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="metric in indicatorTableData" :key="metric.metricKey">
                <td>
                  {{ metric.metricName }}
                  <span v-if="metric.unitLabel" class="metric-unit">({{ metric.unitLabel }})</span>
                </td>
                <td
                  v-for="val in metric.values"
                  :key="val.id"
                  :class="{ 'best-value': val.isBest, 'worst-value': val.isWorst }"
                >
                  {{ val.displayValue }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="placeholder-text">{{ t('simulation.compare.placeholders.noIndicators') }}</p>
      </section>

      <section class="compare-section">
        <h2>{{ t('simulation.compare.sections.cumulativeCharts') }}</h2>
        <div v-if="comparisonChartOptions" class="chart-container-comparison">
          <DataChart :options="comparisonChartOptions" />
        </div>
        <p v-else class="placeholder-text">{{ t('simulation.compare.placeholders.noChart') }}</p>
      </section>
    </div>
    <div v-else class="no-data-message">
      <p>{{ t('simulation.compare.placeholders.noData') }}</p>
      <router-link to="/simulation/history" class="btn btn-secondary">{{ t('simulation.compare.backToHistory') }}</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'; // Added computed
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { getSimulationDetails as apiGetSimulationDetails } from '@/services/api.js'; // Uncommented and to be used
import DataChart from '@/components/DataChart.vue'; // Added Chart import

const route = useRoute();
const { t } = useI18n();
const simulationsToCompare = ref([]); // This will hold the full simulation objects once fetched
const isLoading = ref(false); // Will be true when fetching data
const error = ref(null);
const simulationIds = ref([]); // Stores the IDs from the query parameter

onMounted(() => {
  const idsQueryParam = route.query.ids;
  if (idsQueryParam) {
    simulationIds.value = idsQueryParam.split(',').map(id => id.trim()).filter(id => id);

    if (simulationIds.value.length < 2) {
        error.value = t('simulation.compare.errors.minSelection');
    } else if (simulationIds.value.length > 5) {
        error.value = t('simulation.compare.errors.maxSelection');
    } else {
        fetchComparisonData();
    }
  } else {
    error.value = t('simulation.compare.errors.missingIds');
  }
});

async function fetchComparisonData() {
  isLoading.value = true;
  error.value = null;
  const fetchedSimulations = [];
  try {
    const promises = simulationIds.value.map(id => apiGetSimulationDetails(id));
    const results = await Promise.allSettled(promises);

    results.forEach(result => {
      if (result.status === 'fulfilled' && result.value) {
        if (result.value.status === 'Completed' && result.value.performance_metrics) {
          fetchedSimulations.push(result.value);
        } else {
          console.warn(`Simulation ${result.value.id} is not 'Completed' or lacks results, excluding from comparison.`);
        }
      } else if (result.status === 'rejected') {
        console.error('Failed to fetch simulation details:', result.reason);
      }
    });

    if (fetchedSimulations.length < 2) {
      error.value = t('simulation.compare.errors.notEnoughValid');
      simulationsToCompare.value = [];
    } else {
      simulationsToCompare.value = fetchedSimulations;
    }

  } catch (err) { // Catch any unexpected error from Promise.allSettled or setup
    console.error('Error fetching comparison data:', err);
    error.value = t('simulation.compare.errors.unexpected');
    simulationsToCompare.value = [];
  } finally {
    isLoading.value = false;
  }
}

// Helper function for highlighting in indicatorTableData
function applyMinMaxHighlighting(metricValues, higherIsBetter = true) {
  if (!metricValues || metricValues.length === 0) return metricValues;

  const validValues = metricValues.filter(item => typeof item.value === 'number' && !isNaN(item.value)).map(item => item.value);
  if (validValues.length === 0) return metricValues;

  const minVal = Math.min(...validValues);
  const maxVal = Math.max(...validValues);

  return metricValues.map(item => {
    let isBest = false;
    let isWorst = false;
    if (typeof item.value === 'number' && !isNaN(item.value)) {
      isBest = higherIsBetter ? item.value === maxVal : item.value === minVal;
      isWorst = higherIsBetter ? item.value === minVal : item.value === maxVal;
      // Handle ties: if all values are the same, none are best/worst
      if (minVal === maxVal && validValues.length > 1) {
        isBest = false;
        isWorst = false;
      }
    }
    return { ...item, isBest, isWorst };
  });
}

function formatMetricValue(rawValue, unit) {
  if (typeof rawValue !== 'number' || isNaN(rawValue)) {
    return t('common.notAvailable');
  }
  if (unit === 'percent') {
    return `${(rawValue * 100).toFixed(1)}%`;
  }
  if (unit === 'days') {
    return t('simulation.compare.units.daysValue', { value: rawValue.toFixed(1) });
  }
  return String(rawValue);
}


const indicatorTableData = computed(() => {
  if (simulationsToCompare.value.length < 2) return [];

  const metrics = [
    { key: 'performance_metrics.total_order_count', name: t('simulation.compare.metrics.orderCount'), higherIsBetter: false, unit: null },
    { key: 'performance_metrics.win_rate_percent', name: t('simulation.compare.metrics.winRate'), higherIsBetter: true, unit: 'percent' },
    { key: 'performance_metrics.average_holding_period_days', name: t('simulation.compare.metrics.averageHoldingPeriod'), higherIsBetter: false, unit: 'days' },
    { key: 'performance_metrics.total_profit_sum', name: t('simulation.compare.metrics.totalProfit'), higherIsBetter: true, unit: null },
    { key: 'performance_metrics.profit_loss_ratio', name: t('simulation.compare.metrics.profitLossRatio'), higherIsBetter: true, unit: null },
    { key: 'performance_metrics.average_return_percent_per_trade', name: t('simulation.compare.metrics.averageReturnPerTrade'), higherIsBetter: true, unit: 'percent' }
  ];

  return metrics.map(metric => {
    let values = simulationsToCompare.value.map(sim => {
      // Helper function to get nested value
      const getNestedValue = (obj, path) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
      };
      
      const rawValue = getNestedValue(sim.result, metric.key);
      let displayValue = t('common.notAvailable');
      if (typeof rawValue === 'number' && !isNaN(rawValue)) {
        displayValue = formatMetricValue(rawValue, metric.unit);
      }
      return {
        id: sim.id,
        name: sim.name,
        value: typeof rawValue === 'number' ? rawValue : undefined, // Store numeric value for comparison
        displayValue: displayValue
      };
    });

    // Apply highlighting
    values = applyMinMaxHighlighting(values, metric.higherIsBetter);

    return {
      metricKey: metric.key,
      metricName: metric.name,
      values: values,
      unitLabel: metric.unit ? t(`simulation.compare.units.${metric.unit}`) : ''
    };
  });
});


const comparisonChartOptions = computed(() => {
  // Cumulative performance data is not available in the new performance_metrics format
  // This chart functionality is temporarily disabled
  return null;
});

</script>

<style scoped>
.simulation-compare-page {
  padding: 20px;
  max-width: 1200px;
  margin: 20px auto;
  font-family: Arial, sans-serif;
}

h1 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
}

.loading-message,
.error-message,
.no-data-message {
  text-align: center;
  padding: 30px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.loading-message {
  color: #555;
}

.error-message {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 15px;
}
.error-message .btn {
  margin-top: 15px;
}

.no-data-message {
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  color: #856404;
  padding: 15px;
}
.no-data-message .btn {
  margin-top: 15px;
}

.comparison-intro {
  text-align: center;
  font-size: 1.1em;
  margin-bottom: 25px;
  color: #333;
}

.compare-section {
  background-color: #f9f9f9;
  padding: 25px;
  margin-bottom: 30px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}

.compare-section h2 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #0056b3;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
  font-size: 1.5em;
}

.table-responsive {
  overflow-x: auto; /* Allows table to be scrolled horizontally on small screens */
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
  font-size: 0.9em;
}
.comparison-table th, .comparison-table td {
  border: 1px solid #ddd;
  padding: 10px 12px;
  text-align: left;
  vertical-align: middle;
}
.comparison-table thead th {
  background-color: #e9ecef;
  color: #333;
  font-weight: bold;
  white-space: nowrap;
}
.comparison-table .sim-name-header {
  min-width: 150px; /* Ensure simulation name column has decent width */
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.comparison-table tbody tr:nth-child(even) {
  background-color: #ffffff;
}
.comparison-table tbody tr:hover {
  background-color: #f1f1f1;
}
.metric-unit {
  font-size: 0.85em;
  color: #666;
}
.best-value {
  background-color: rgba(40, 167, 69, 0.1); /* Light green background */
  color: #155724; /* Dark green text */
  font-weight: bold;
}
.worst-value {
  background-color: rgba(220, 53, 69, 0.1); /* Light red background */
  color: #721c24; /* Dark red text */
  font-weight: bold;
}


.placeholder-text {
  font-style: italic;
  color: #777;
  text-align: center;
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
  border: 1px dashed #ccc;
}

.chart-container-comparison {
  width: 100%;
  height: 450px; /* Default height for the comparison chart */
  margin-top: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  background-color: #fff; /* White background for chart area */
}


.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #007bff; /* Theme color */
  animation: spin 1s linear infinite;
  margin: 10px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* General button styling if not globally available */
.btn {
  display: inline-block;
  font-weight: 400;
  color: #212529;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  background-color: transparent;
  border: 1px solid transparent;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.btn-secondary {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  color: #fff;
  background-color: #5a6268;
  border-color: #545b62;
}
</style>
